import { Button } from '@/components/ui/button'
import { Drawer, DrawerContentRight } from '@/components/ui/drawer'
import { SectionHeading } from '@/components/ui/section'
import { useSettingsPanelState } from '@/hooks/useSettingsPanelState'
import { useDataSources } from '@/queries/data-sources/useDataSources'
import { useDeleteDataSource } from '@/queries/data-sources/useDeleteDataSource'
import { useStytchOrganization } from '@stytch/react/b2b'
import { LayoutGrid, Plus, Table2 } from 'lucide-react'

import { DataSourceRecordPanel } from './DataSourceRecordPanel'
import { DataSourcesCards } from './DataSourcesCards'
import { DataSourcesTable } from './DataSourcesTable'

import type { WorkspaceDataSource } from 'honeygrid-types'

export const DataSourcesList = () => {
	const { organization } = useStytchOrganization()
	const { data: dataSources, isLoading } = useDataSources(organization?.organization_id)
	const { mutate: deleteDataSource } = useDeleteDataSource(organization?.organization_id)
	const { state, openRecord, openCreate, closePanel, setView } =
		useSettingsPanelState('data-sources')

	const selectedDataSource = state.recordId
		? dataSources?.find((d) => String(d.id) === state.recordId)
		: undefined

	const handleSelect = (ds: WorkspaceDataSource) => {
		openRecord(String(ds.id))
	}

	const handleAdd = () => {
		openCreate()
	}

	const listPanel = (
		<div className="flex flex-col h-full">
			{/* Header with title and controls in one line */}
			<div className="flex items-center justify-between mb-4">
				<SectionHeading>Data Sources</SectionHeading>
				<div className="flex items-center gap-3">
					{/* View Toggle */}
					<div className="flex border rounded-md">
						<Button
							variant={state.view === 'table' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('table')}
							className="rounded-r-none border-r"
						>
							<Table2 className="h-4 w-4" />
						</Button>
						<Button
							variant={state.view === 'cards' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('cards')}
							className="rounded-l-none"
						>
							<LayoutGrid className="h-4 w-4" />
						</Button>
					</div>
					<Button onClick={handleAdd} size="sm" className="cursor-pointer">
						<Plus className="h-4 w-4 mr-2" />
						Add Data Source
					</Button>
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-auto">
				{isLoading ? (
					<div className="text-center py-8 text-muted-foreground">Loading data sources...</div>
				) : state.view === 'table' ? (
					<DataSourcesTable
						dataSources={dataSources || []}
						onSelectDataSource={handleSelect}
						onAddDataSource={handleAdd}
						selectedDataSourceId={state.recordId || undefined}
					/>
				) : (
					<DataSourcesCards
						dataSources={dataSources || []}
						onSelectDataSource={handleSelect}
						onAddDataSource={handleAdd}
						selectedDataSourceId={state.recordId || undefined}
						onDeleteDataSource={(id) => deleteDataSource(String(id))}
					/>
				)}
			</div>
		</div>
	)

	return (
		<div className="flex flex-col h-full">
			{listPanel}
			<Drawer open={state.isOpen} onOpenChange={(open) => !open && closePanel()} direction="right">
				<DrawerContentRight>
					<DataSourceRecordPanel
						onClose={closePanel}
						dataSource={selectedDataSource}
						isNew={state.mode === 'new'}
					/>
				</DrawerContentRight>
			</Drawer>
		</div>
	)
}
